import _ from "lodash";
import type { Agreement, RawAttribute } from "@/api";
import type { Attribute, BaseAttribute } from "@/attributes";
import { hasAccess, removeAccess, setAccess, type AccessGroup } from "@/access";

// The access page edits these two together: an agreement linked to a legacy
// access group (through its site tag, which the group's sites are synced
// with) is enabled and disabled along with the group's bit.
export const AGREEMENTS_ATTRIBUTE = "AGREEMENTS";

export interface AccessAttributes {
    agreements: Attribute<number[]>;
    access: BaseAttribute<AccessGroup>;
}

const parseIds = (value: string): number[] =>
    _.uniq(
        value
            .split(",")
            .map((token) => Number(token.trim()))
            .filter((id) => Number.isInteger(id) && id > 0)
    );

// A user the API sends without the attribute cannot have it set either.
const MISSING: RawAttribute = {
    value: "",
    default: "",
    inherit: false,
    inherit_value: "",
    inherit_lineage: [],
    whocanset: null,
    whocanset_lineage: null,
};

export const parseAgreementsAttribute = (
    attribute: RawAttribute | undefined
): Attribute<number[]> => {
    const raw = attribute ?? MISSING;

    return {
        key: AGREEMENTS_ATTRIBUTE,
        value: parseIds(raw.value),
        default: parseIds(raw.default),
        inherit: raw.inherit,
        inheritValue: parseIds(raw.inherit_value),
        inheritLineage: raw.inherit_lineage,
        whocanset: raw.whocanset,
        whocansetLineage: raw.whocanset_lineage,
        getActiveValue() {
            return this.inherit ? this.inheritValue : this.value;
        },
        getValueRepresentation() {
            return this.value.join(",");
        },
        canBeSet() {
            return this.whocanset !== null;
        },
    };
};

export const areAgreementsEqual = (
    first: BaseAttribute<number[]>,
    second: BaseAttribute<number[]>
): boolean =>
    first.inherit === second.inherit &&
    first.whocanset === second.whocanset &&
    _.isEqual([...first.value].sort(), [...second.value].sort());

export const accessGroupOf = (
    agreement: Agreement | undefined
): bigint | null => {
    const group = agreement?.tag?.access_group;
    return group ? BigInt(group.access_groups) : null;
};

// Inherited or not ours to set: the coupling leaves it as it is.
const isLocked = (attribute: BaseAttribute<unknown>): boolean =>
    attribute.inherit || !attribute.canBeSet();

const enabledIds = ({ agreements }: AccessAttributes): number[] =>
    agreements.getActiveValue();

export const setAgreement = (
    attributes: AccessAttributes,
    catalogue: Agreement[],
    idAgreement: number,
    enabled: boolean
): void => {
    const { agreements, access } = attributes;

    if (isLocked(agreements)) {
        return;
    }

    agreements.value = enabled
        ? _.uniq([...agreements.value, idAgreement])
        : agreements.value.filter((id) => id !== idAgreement);

    const group = accessGroupOf(
        catalogue.find((a) => a.id_agreement === idAgreement)
    );

    if (group === null || isLocked(access)) {
        return;
    }

    // Agreements on one tag share its group, so the bit stays while any of
    // them is still enabled.
    const stillWanted = catalogue.some(
        (a) =>
            accessGroupOf(a) === group &&
            agreements.value.includes(a.id_agreement)
    );

    access.value.accessGroup = stillWanted
        ? setAccess(access.value.accessGroup, group)
        : removeAccess(access.value.accessGroup, group);
};

export const setAccessGroup = (
    attributes: AccessAttributes,
    catalogue: Agreement[],
    bit: bigint,
    enabled: boolean
): void => {
    const { agreements, access } = attributes;

    if (isLocked(access)) {
        return;
    }

    access.value.accessGroup = enabled
        ? setAccess(access.value.accessGroup, bit)
        : removeAccess(access.value.accessGroup, bit);

    if (isLocked(agreements)) {
        return;
    }

    const linked = catalogue
        .filter((a) => accessGroupOf(a) === bit)
        .map((a) => a.id_agreement);

    if (!enabled) {
        agreements.value = agreements.value.filter(
            (id) => !linked.includes(id)
        );
        return;
    }

    // With several agreements on the group's tag there is no telling which
    // terms were meant; the mismatch warning asks for one to be picked.
    const alreadyCovered = linked.some((id) => agreements.value.includes(id));
    if (linked.length === 1 && !alreadyCovered) {
        agreements.value = [...agreements.value, linked[0]];
    }
};

// When the user inherits both, as saved, the access groups follow agreements
// on and off inheritance, so the two go on being edited together. Otherwise
// the access groups keep their own setting.
export const setAgreementsInherit = (
    { agreements, access }: AccessAttributes,
    saved: AccessAttributes,
    inherit: boolean
): void => {
    if (!agreements.canBeSet()) {
        return;
    }

    agreements.inherit = inherit;

    if (saved.agreements.inherit && saved.access.inherit && access.canBeSet()) {
        access.inherit = inherit;
    }
};

export interface Mismatch {
    group: bigint;
    groupName: string;
    agreements: Agreement[];
    // Whether any agreement on the group is enabled, and whether its bit is.
    agreementEnabled: boolean;
    groupSet: boolean;
}

// Linked agreements and groups that disagree, as the user ends up with them:
// inherited values included, since that is where they can drift apart.
export const findMismatches = (
    attributes: AccessAttributes,
    catalogue: Agreement[]
): Mismatch[] => {
    const ids = enabledIds(attributes);
    const bits = attributes.access.getActiveValue().accessGroup;
    const byGroup = new Map<bigint, Agreement[]>();

    for (const agreement of catalogue) {
        const group = accessGroupOf(agreement);
        if (group !== null) {
            byGroup.set(group, [...(byGroup.get(group) ?? []), agreement]);
        }
    }

    return [...byGroup.entries()]
        .map(([group, linked]) => ({
            group,
            groupName: linked[0].tag?.access_group?.name ?? `${group}`,
            agreements: linked,
            agreementEnabled: linked.some((a) => ids.includes(a.id_agreement)),
            groupSet: hasAccess(group, bits),
        }))
        .filter((m) => m.agreementEnabled !== m.groupSet);
};
